import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import api from '@/services/api';
import { useToast } from '@/components/ui/ToastProvider';
import { filenameFromContentDisposition } from '@/lib/contentDisposition';
import { dropSearchParams } from '@/lib/searchParams';
import {
  IMPORT_HANDOFF_QUERY_PARAM,
  IMPORT_HANDOFF_QUERY_VALUE,
  clearImportHandoff,
  getPendingImportHandoff,
} from '@/lib/importHandoff';

/** Ime, ko zaledje ne pošlje `Content-Disposition`. */
const FALLBACK_FILENAME = 'popis.xlsx';

/**
 * T3077: enkraten prevzem predane datoteke. Žeton se ob uspehu na strežniku
 * porabi, zato ga sme klicatelj poslati samo enkrat.
 */
export async function claimImportHandoff(token: string): Promise<File> {
  const res = await api.post<Blob>(
    'import/handoff/claim/',
    { token },
    { responseType: 'blob' },
  );
  const header = res.headers['content-disposition'];
  const name =
    filenameFromContentDisposition(typeof header === 'string' ? header : null) ??
    FALLBACK_FILENAME;
  const contentType = res.headers['content-type'];
  const type = res.data.type || (typeof contentType === 'string' ? contentType : '');
  return new File([res.data], name, { type });
}

/**
 * T3077 — popis projekta s `?uvoz=predaja` prevzame datoteko, ki jo je
 * spletna stran predala aplikaciji (`lib/importHandoff`), in odpre čarovnik.
 *
 * - uspeh: predaja se pobriše, `openWizard(file)`;
 * - 404 / 400 (žeton neznan, potekel, porabljen ali napačne oblike): predaja
 *   se pobriše, obvestilo »ni več na voljo«, `openWizard()` s praznim čarovnikom;
 * - drugo (429, 5xx, brez povezave): predaja ostane (strežnik jo še hrani),
 *   obvestilo z gumbom »Poskusi znova«.
 *
 * Parameter se odstrani v vsakem primeru. Brez parametra ali brez čakajoče
 * predaje se ne zgodi nič. Ref varuje pred dvojnim prevzemom ob dvojnem
 * efektu v StrictMode — drugi klic bi enkratni žeton našel porabljenega.
 */
export function useImportHandoffClaim(openWizard: (file?: File) => void): void {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const startedRef = useRef(false);
  const openWizardRef = useRef(openWizard);
  useEffect(() => {
    openWizardRef.current = openWizard;
  });

  const requested =
    searchParams.get(IMPORT_HANDOFF_QUERY_PARAM) === IMPORT_HANDOFF_QUERY_VALUE;

  const run = useCallback(
    (token: string) => {
      // Notranja funkcija, da jo gumb »Poskusi znova« lahko pokliče znova.
      function attempt(tok: string) {
        claimImportHandoff(tok).then(
          (file) => {
            clearImportHandoff();
            openWizardRef.current(file);
          },
          (err: unknown) => {
            const status = axios.isAxiosError(err) ? err.response?.status : undefined;
            if (status === 404 || status === 400) {
              clearImportHandoff();
              toast({
                type: 'info',
                title: t('import_v2.handoff.unavailable_title'),
                message: t('import_v2.handoff.unavailable_desc'),
              });
              openWizardRef.current();
              return;
            }
            toast({
              type: 'error',
              title: t('import_v2.handoff.failed_title'),
              message: t('import_v2.handoff.failed_desc'),
              action: {
                label: t('import_v2.handoff.retry'),
                onClick: () => {
                  const pending = getPendingImportHandoff();
                  if (pending) attempt(pending.token);
                },
              },
            });
          },
        );
      }
      attempt(token);
    },
    [t, toast],
  );

  useEffect(() => {
    if (!requested) return;
    setSearchParams((prev) => dropSearchParams(prev, [IMPORT_HANDOFF_QUERY_PARAM]), {
      replace: true,
    });
    if (startedRef.current) return;
    const pending = getPendingImportHandoff();
    if (!pending) return;
    startedRef.current = true;
    run(pending.token);
  }, [requested, setSearchParams, run]);
}
